import {
    FileDownloadCard,
    parseAdminFilePayload,
} from '@apps/web/routes/_app/admin/agent/artifacts/file';
import {
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRef,
    type AdminArtifactRendererProps,
    artifactRegistryKey,
    JsonValue,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

export function InlineAdminArtifactChip({
    artifactRef,
    onOpenArtifact,
}: {
    readonly artifactRef: AdminArtifactRef;
    readonly onOpenArtifact?: () => void;
}) {
    return (
        <div className="border-border/60 bg-muted/30 flex min-w-0 items-center justify-between gap-3 rounded-md border px-3 py-2">
            <div className="min-w-0">
                <p className="text-foreground truncate text-xs font-medium">
                    {artifactRef.title}
                </p>
                <p className="text-muted-foreground truncate font-mono text-[10px]">
                    {artifactRef.artifact_type}@
                    {artifactRef.artifact_version ?? 1}
                </p>
            </div>
            <button
                type="button"
                className="text-foreground shrink-0 text-[11px] font-medium underline underline-offset-2 disabled:cursor-default disabled:no-underline disabled:opacity-60"
                disabled={!onOpenArtifact}
                onClick={onOpenArtifact}
            >
                Open in Artifacts
            </button>
        </div>
    );
}

export function UnsupportedAdminArtifactRenderer({
    artifact,
}: AdminArtifactRendererProps) {
    const registryKey = artifactRegistryKey(artifact);
    const filePayload = parseAdminFilePayload(artifact.payload);
    if (filePayload) {
        return (
            <ArtifactFrame
                artifact={artifact}
                title={artifact.title}
                status={
                    <ArtifactLifecycleStatusBadge
                        lifecycleStatus={artifact.lifecycle_status}
                    />
                }
            >
                <ArtifactEvidence artifact={artifact} />
                <FileDownloadCard payload={filePayload} />
            </ArtifactFrame>
        );
    }
    return (
        <ArtifactFrame
            artifact={artifact}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <div className="border-border/60 bg-muted/30 space-y-2 rounded-md border border-dashed px-3 py-2">
                <p className="text-foreground text-[12px] font-medium">
                    No dedicated renderer for this artifact type
                </p>
                <p className="text-muted-foreground text-[11px] leading-relaxed break-words">
                    The raw payload is available below. Add a renderer to the
                    admin artifact registry to give this type a purpose-built
                    view.
                </p>
                <dl className="grid gap-x-3 gap-y-1 text-[11px]">
                    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                        <dt className="text-muted-foreground/70">
                            Artifact id
                        </dt>
                        <dd className="text-foreground min-w-0 font-mono break-all">
                            {artifact.id}
                        </dd>
                    </div>
                    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                        <dt className="text-muted-foreground/70">
                            Renderer key
                        </dt>
                        <dd className="text-foreground min-w-0 font-mono break-words">
                            {registryKey}
                        </dd>
                    </div>
                </dl>
            </div>
            <ArtifactEvidence artifact={artifact} />
            <details className="group">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                    Raw JSON payload
                </summary>
                <div className="border-border/60 mt-1.5 border-l pl-3">
                    <JsonValue value={artifact.payload} />
                </div>
            </details>
        </ArtifactFrame>
    );
}
